'use client'

import { ComponentProps, memo, useCallback, useContext } from 'react'
import clsx from 'clsx'
import { usePathname } from 'next/navigation'

import { NavigationOpenables } from 'shared/types/navigation'
import { Drawer } from 'shared/ui/Drawer'
import {
  OfflineContext,
  OfflineContextType
} from 'shared/ui/Offline/OfflineContext'
import toast from 'shared/ui/utils/toast'
import { noop } from 'shared/utils/common'

import { CartPorts } from '../ports'
import useCartStore, { useDisplayCart } from '../store/cartStore'
import CartSummaryHeader from '../submodules/summary/components/CartSummaryHeader'
import { Cart } from '../types'

import CartDrawerControls from './CartDrawerControls'
import CartDrawerTabletContent from './CartDrawerTabletContent'
import CartDrawerWebContent from './CartDrawerWebContent'

import styles from './CartDrawer.module.scss'

interface CartDrawerProps {
  open: boolean
  setOpen: (name: NavigationOpenables, value: boolean) => void
  ecoVariant?: boolean
  isRibbonActive?: boolean
  ports: CartPorts
}

const CartDrawer = ({
  open,
  setOpen,
  isRibbonActive = false,
  ports
}: CartDrawerProps) => {
  const pathname = usePathname()
  const { refreshCart, flushPendingOperations, hasPendingOperations } =
    useCartStore()
  const displayCart = useDisplayCart()
  const { isOnline } = useContext(OfflineContext) as OfflineContextType

  const productsList = displayCart?.items || []
  const isCartEditMode = displayCart?.editMode

  const handleClose = useCallback(() => {
    setOpen('cart', false)
  }, [setOpen])

  const toggleDrawer = useCallback(async () => {
    const newState = !open

    setOpen('cart', newState)

    if (newState) {
      if (hasPendingOperations()) {
        await flushPendingOperations()
      } else {
        const value = displayCart?.totalValueGross?.value

        refreshCart()
          .then((resp: Cart | null) => {
            const newValue = resp?.totalValueGross?.value || 0

            if (value !== newValue) {
              toast.success('Ceny w koszyku zostały ponownie przeliczone')
            }
          })
          .catch(noop)
      }
    }
  }, [
    open,
    setOpen,
    displayCart,
    refreshCart,
    flushPendingOperations,
    hasPendingOperations
  ])

  return (
    <>
      <CartDrawerControls onClick={toggleDrawer} open={open} />
      <StyledDrawer
        placement='right'
        open={open}
        onClose={handleClose}
        id='cart_drawer'
        size='large'
        $isRibbonActive={isRibbonActive}
      >
        <CartSummaryHeader
          productsCount={displayCart?.items?.length || 0}
          hideOnMobile
          isInDrawer
        />
        <CartDrawerTabletContent
          productsList={productsList}
          asPath={pathname || ''}
          isCartEditMode={isCartEditMode}
          isOnline={isOnline}
          handleModalClose={handleClose}
          ports={ports}
        />
        <CartDrawerWebContent
          productsList={productsList}
          asPath={pathname || ''}
          isCartEditMode={isCartEditMode}
          isOnline={isOnline}
          handleModalClose={handleClose}
          ports={ports}
        />
      </StyledDrawer>
    </>
  )
}

export default memo(CartDrawer)

const StyledDrawer = ({
  $isRibbonActive,
  className,
  ...props
}: ComponentProps<typeof Drawer> & { $isRibbonActive: boolean }) => (
  <Drawer
    className={clsx(
      styles.cartDrawer,
      {
        [styles.cartDrawerWithRibbon]: $isRibbonActive,
        [styles.cartDrawerNoRibbon]: !$isRibbonActive
      },
      className
    )}
    {...props}
  />
)
